import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import './Nonogram.css';

const Nonogram = () => {
  const [puzzle, setPuzzle] = useState(null);
  const [playerGrid, setPlayerGrid] = useState([]);
  const [isComplete, setIsComplete] = useState(false);
  const [selectedPuzzle, setSelectedPuzzle] = useState(0);

  // Predefined puzzles (5x5 for simplicity) - memoized to prevent recreation
  const puzzles = useMemo(() => [
    {
      name: "Heart",
      solution: [
        [0, 1, 0, 1, 0],
        [1, 1, 1, 1, 1],
        [1, 1, 1, 1, 1],
        [0, 1, 1, 1, 0],
        [0, 0, 1, 0, 0]
      ]
    },
    {
      name: "House",
      solution: [
        [0, 0, 1, 0, 0],
        [0, 1, 1, 1, 0],
        [1, 1, 1, 1, 1],
        [1, 0, 1, 0, 1],
        [1, 0, 1, 0, 1]
      ]
    },
    {
      name: "Cross",
      solution: [
        [0, 0, 1, 0, 0],
        [0, 0, 1, 0, 0],
        [1, 1, 1, 1, 1],
        [0, 0, 1, 0, 0],
        [0, 0, 1, 0, 0]
      ]
    },
    {
      name: "Diamond",
      solution: [
        [0, 0, 1, 0, 0],
        [0, 1, 1, 1, 0],
        [1, 1, 1, 1, 1],
        [0, 1, 1, 1, 0],
        [0, 0, 1, 0, 0]
      ]
    },
    {
      name: "Simple",
      solution: [
        [1, 1, 1, 0, 0],
        [1, 1, 1, 0, 0],
        [1, 1, 1, 0, 0],
        [0, 0, 0, 0, 0],
        [0, 0, 0, 0, 0]
      ]
    },
    {
      name: "Arrow",
      solution: [
        [0, 0, 1, 0, 0],
        [0, 1, 1, 1, 0],
        [1, 1, 1, 1, 1],
        [0, 1, 1, 1, 0],
        [0, 1, 1, 1, 0]
      ]
    },
    {
      name: "Smiley",
      solution: [
        [0, 1, 1, 1, 0],
        [1, 0, 1, 0, 1],
        [1, 0, 1, 0, 1],
        [1, 1, 0, 1, 1],
        [0, 1, 1, 1, 0]
      ]
    },
    {
      name: "Tree",
      solution: [
        [0, 0, 1, 0, 0],
        [0, 1, 1, 1, 0],
        [1, 1, 1, 1, 1],
        [0, 0, 1, 0, 0],
        [0, 1, 1, 1, 0]
      ]
    },
    {
      name: "Star",
      solution: [
        [0, 0, 1, 0, 0],
        [0, 1, 1, 1, 0],
        [1, 1, 1, 1, 1],
        [0, 1, 0, 1, 0],
        [1, 0, 0, 0, 1]
      ]
    },
    {
      name: "Butterfly",
      solution: [
        [1, 0, 1, 0, 1],
        [1, 1, 1, 1, 1],
        [0, 1, 1, 1, 0],
        [1, 1, 1, 1, 1],
        [1, 0, 1, 0, 1]
      ]
    },
    {
      name: "Checkerboard",
      solution: [
        [1, 0, 1, 0, 1],
        [0, 1, 0, 1, 0],
        [1, 0, 1, 0, 1],
        [0, 1, 0, 1, 0],
        [1, 0, 1, 0, 1]
      ]
    },
    {
      name: "Letter T",
      solution: [
        [1, 1, 1, 1, 1],
        [0, 0, 1, 0, 0],
        [0, 0, 1, 0, 0],
        [0, 0, 1, 0, 0],
        [0, 0, 1, 0, 0]
      ]
    },
    {
      name: "Hourglass",
      solution: [
        [1, 1, 1, 1, 1],
        [0, 1, 1, 1, 0],
        [0, 0, 1, 0, 0],
        [0, 1, 1, 1, 0],
        [1, 1, 1, 1, 1]
      ]
    },
    {
      name: "Stairs",
      solution: [
        [1, 0, 0, 0, 0],
        [1, 1, 0, 0, 0],
        [1, 1, 1, 0, 0],
        [1, 1, 1, 1, 0],
        [1, 1, 1, 1, 1]
      ]
    },
    {
      name: "Frame",
      solution: [
        [1, 1, 1, 1, 1],
        [1, 0, 0, 0, 1],
        [1, 0, 0, 0, 1],
        [1, 0, 0, 0, 1],
        [1, 1, 1, 1, 1]
      ]
    }
  ], []);

  const generateClues = useCallback((solution) => {
    const size = solution.length;
    const rowClues = [];
    const colClues = [];

    // Generate row clues
    for (let i = 0; i < size; i++) {
      const clue = [];
      let count = 0;
      for (let j = 0; j < size; j++) {
        if (solution[i][j] === 1) {
          count++;
        } else {
          if (count > 0) {
            clue.push(count);
            count = 0;
          }
        }
      }
      if (count > 0) clue.push(count);
      rowClues.push(clue.length === 0 ? [0] : clue);
    }

    // Generate column clues
    for (let j = 0; j < size; j++) {
      const clue = [];
      let count = 0;
      for (let i = 0; i < size; i++) {
        if (solution[i][j] === 1) {
          count++;
        } else {
          if (count > 0) {
            clue.push(count);
            count = 0;
          }
        }
      }
      if (count > 0) clue.push(count);
      colClues.push(clue.length === 0 ? [0] : clue);
    }

    return { rowClues, colClues };
  }, []);

  const initializePuzzle = useCallback((puzzleIndex) => {
    const selectedPuzzleData = puzzles[puzzleIndex];
    const { rowClues, colClues } = generateClues(selectedPuzzleData.solution);
    
    setPuzzle({
      ...selectedPuzzleData,
      rowClues,
      colClues
    });

    // Initialize player grid (0 = empty, 1 = filled, 2 = marked as empty)
    setPlayerGrid(Array(5).fill().map(() => Array(5).fill(0)));
    setIsComplete(false);
  }, [puzzles, generateClues]);

  const handleCellClick = (row, col, isRightClick = false) => {
    if (isComplete) return;

    const newGrid = playerGrid.map(r => [...r]);
    
    if (isRightClick) {
      // Right click cycles: empty -> marked empty -> empty
      newGrid[row][col] = newGrid[row][col] === 2 ? 0 : 2;
    } else {
      // Left click cycles: empty -> filled -> empty
      newGrid[row][col] = newGrid[row][col] === 1 ? 0 : 1;
    }

    setPlayerGrid(newGrid);

    // Check if puzzle is complete
    checkCompletion(newGrid);
  };

  const checkCompletion = (grid) => {
    if (!puzzle) return;

    const isCorrect = grid.every((row, i) =>
      row.every((cell, j) => {
        const shouldBeFilled = puzzle.solution[i][j] === 1;
        const isFilled = cell === 1;
        return shouldBeFilled === isFilled;
      })
    );

    if (isCorrect) {
      setIsComplete(true);
    }
  };

  const resetPuzzle = () => {
    setPlayerGrid(Array(5).fill().map(() => Array(5).fill(0)));
    setIsComplete(false);
  };

  const getCellClass = (row, col) => {
    const cell = playerGrid[row][col];
    let className = 'nonogram-cell';
    
    if (cell === 1) className += ' filled';
    else if (cell === 2) className += ' marked-empty';
    
    return className;
  };

  const getCellContent = (row, col) => {
    const cellValue = playerGrid[row][col];
    if (cellValue === 1) return '■';
    if (cellValue === 2) return '×';
    return '';
  };

  useEffect(() => {
    initializePuzzle(selectedPuzzle);
  }, [selectedPuzzle, initializePuzzle]);

  if (!puzzle) return <div>Loading...</div>;

  return (
    <div className="game-container">
      <div className="game-header">
        <h1 className="game-title-header">⬛ Nonogram</h1>
        <div className="game-instructions">
          Use number clues to fill cells and reveal the hidden picture. Left click to fill, right click to mark empty.
        </div>
      </div>

      <div className="game-board">
        <div className="score-display">
          {isComplete ? `🎉 You solved the ${puzzle.name} puzzle!` : `Puzzle: ${puzzle.name}`}
        </div>

        <div className="puzzle-selector">
          <select 
            value={selectedPuzzle} 
            onChange={(e) => setSelectedPuzzle(parseInt(e.target.value))}
            className="btn btn-primary"
          >
            {puzzles.map((puzzle, index) => (
              <option key={index} value={index}>{puzzle.name}</option>
            ))}
          </select>
        </div>

        <div className="game-play-area">
          <div className="nonogram-puzzle">
            <div className="column-clues">
              <div className="corner-cell"></div>
              {puzzle.colClues.map((clue, index) => (
                <div key={index} className="clue-cell column-clue">
                  {clue.map((num, i) => (
                    <div key={i} className="clue-number">{num}</div>
                  ))}
                </div>
              ))}
            </div>

            {playerGrid.map((row, rowIndex) => (
              <div key={rowIndex} className="nonogram-row">
                <div className="clue-cell row-clue">
                  {puzzle.rowClues[rowIndex].map((num, i) => (
                    <span key={i} className="clue-number">{num}</span>
                  ))}
                </div>
                {row.map((cell, colIndex) => (
                  <div
                    key={colIndex}
                    className={getCellClass(rowIndex, colIndex)}
                    onClick={() => handleCellClick(rowIndex, colIndex)}
                    onContextMenu={(e) => {
                      e.preventDefault();
                      handleCellClick(rowIndex, colIndex, true);
                    }}
                  >
                    {getCellContent(rowIndex, colIndex)}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="game-controls">
        <button className="btn btn-primary" onClick={resetPuzzle}>
          Reset Puzzle
        </button>
        <Link to="/" className="btn btn-secondary">
          Back to Home
        </Link>
      </div>
    </div>
  );
};

export default Nonogram;